import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { SidebarInfoCard } from '@/components/ui/SidebarInfoCard';
import styles from './styles.module.scss';

export interface TournamentStat {
  label: string;
  value: string | number;
}

export interface TournamentInfoCardProps {
  /** League/tournament logo URL */
  logoUrl?: string;
  /** Alt text for logo */
  logoAlt?: string;
  /** Main title (usually league name) */
  title: string;
  /** Subtitle (usually tournament name) */
  subtitle?: string;
  /**
   * When set, the subtitle renders as a link to this route with a trailing
   * chevron (touch devices have no hover affordance, so the chevron is the
   * only "this is tappable" cue).
   */
  subtitleTo?: string;
  /** Show Active/Finished badge */
  showBadge?: boolean;
  /** Is tournament active */
  isActive?: boolean;
  /** Stats to display */
  stats?: TournamentStat[];
  /** Stats layout: 'row' for horizontal, 'column' for vertical */
  statLayout?: 'row' | 'column';
  /** Description text */
  description?: string;
  /** Show description section */
  showDescription?: boolean;
  /** Additional className */
  className?: string;
  /** Collapsible on mobile - shows only header, stats on tap/hover */
  collapsibleOnMobile?: boolean;
}

/**
 * Reusable tournament info card component.
 * Extends SidebarCard with tournament-specific header, stats, and collapsible functionality.
 * Used on TournamentDetailPage and LeaderboardPage with different configurations.
 */
export const TournamentInfoCard: React.FC<TournamentInfoCardProps> = ({
  logoUrl,
  logoAlt = 'League',
  title,
  subtitle,
  subtitleTo,
  showBadge = false,
  isActive = true,
  stats = [],
  statLayout = 'column',
  description,
  showDescription = false,
  className,
  collapsibleOnMobile = false,
}) => {
  const isMobile = useBreakpointDown('lg');
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldCollapse = collapsibleOnMobile && isMobile;
  const showStats = !shouldCollapse || isExpanded;

  const handleToggle = () => {
    if (shouldCollapse) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <SidebarInfoCard className={`${styles.tournamentInfoCard} ${className || ''}`}>
      <div
        className={`${styles.header} ${shouldCollapse ? styles.clickable : ''}`}
        onClick={handleToggle}
        role={shouldCollapse ? 'button' : undefined}
        tabIndex={shouldCollapse ? 0 : undefined}
        onKeyDown={shouldCollapse ? (e) => e.key === 'Enter' && handleToggle() : undefined}
      >
        {logoUrl && (
          <div className={styles.icon}>
            <img src={logoUrl} alt={logoAlt} />
          </div>
        )}
        <div className={styles.titleSection}>
          <h1 className={styles.title}>{title}</h1>
          {subtitle && (
            subtitleTo ? (
              <Link
                to={subtitleTo}
                className={`${styles.subtitle} ${styles.subtitleLink}`}
                // The wrapping header doubles as the collapse toggle on mobile;
                // keep a tap on the link purely a navigation.
                onClick={(e) => e.stopPropagation()}
              >
                <span>{subtitle}</span>
                <i className="icon-chevron-right" aria-hidden="true" />
              </Link>
            ) : (
              <p className={styles.subtitle}>{subtitle}</p>
            )
          )}
        </div>
        {showBadge && (
          <span className={`${styles.badge} ${!isActive ? styles.inactive : ''}`}>
            {isActive ? 'Active' : 'Finished'}
          </span>
        )}
        {shouldCollapse && (
          <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
            ▼
          </span>
        )}
      </div>

      {stats.length > 0 && showStats && (
        <div className={`${styles.statsSection} ${shouldCollapse ? styles.collapsible : ''}`}>
          <div className={`${styles.statsGrid} ${styles[`layout-${statLayout}`]}`}>
            {stats.map((stat, index) => (
              <div key={index} className={styles.statItem}>
                {statLayout === 'column' ? (
                  <>
                    <div className={styles.statValue}>{stat.value}</div>
                    <div className={styles.statLabel}>{stat.label}</div>
                  </>
                ) : (
                  <>
                    <span className={styles.statLabel}>{stat.label}</span>
                    <span className={styles.statValue}>{stat.value}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showDescription && description && showStats && (
        <div className={styles.descriptionSection}>
          <h3 className={styles.descriptionTitle}>Description</h3>
          <p className={styles.descriptionText}>{description}</p>
        </div>
      )}
    </SidebarInfoCard>
  );
};

export default TournamentInfoCard;
